import { useFetch } from "~vue/api";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, ref } from "vue";

export interface SearchResultMessage {
    id: number;
    role: string;
    content: string;
    when: string;
}

export interface SearchResultSession {
    uuid: string;
    title: string | null;
    url: string;
    user_message_count: number;
}

export type SearchResultType = "chat" | "uploaded_doc" | "created_doc" | "tool_output";

export interface SearchResultItem {
    relevance: number;
    matched_phrase: string;
    result_type: SearchResultType;
    message: SearchResultMessage;
    session: SearchResultSession;
}

export interface SearchMetadata {
    message_count: number;
    session_count: number;
    tokens_used: number;
    max_tokens: number;
    max_tokens_hit: boolean;
    max_message_count_hit: boolean;
    truncated_message_count: number;
    dropped_message_count: number;
    full_history_included: boolean;
    date_range_oldest: string | null;
    date_range_newest: string | null;
    llm_error: boolean;
    hallucination_count: number;
}

export interface ConversationSearchResponse {
    results: SearchResultItem[];
    metadata: SearchMetadata;
}

export type SearchOptions = {
    scope?: string;
    roles?: string[];
    purposes?: string[];
    content_filter?: string;
    sources?: ("v1" | "v2")[];
};

export interface GroupedSearchResult {
    session: SearchResultSession;
    results: SearchResultItem[];
    topRelevance: number;
    matchCount: number;
    latestDate: string;
    topResult: SearchResultItem;
}

// Per-tab cache of the last completed search so back-navigation restores state
// without firing another LLM call. Cache is intentionally session-scoped — closing
// the tab clears it.
const STORAGE_KEY = "conversation-search-state";

interface PersistedState {
    query: string;
    response: ConversationSearchResponse | null;
    hasSearched: boolean;
    searchCount: number;
    lastSearchId: string;
}

export function useConversationSearch(opts?: { maxAttempts?: number }) {
    const maxAttempts = opts?.maxAttempts ?? 3;
    const query = ref("");
    const searchPayload = ref<{ search_id: string; query: string } | null>(null);
    const hasSearched = ref(false);
    const searchCount = ref(0);
    const lastSearchId = ref("");
    const responseSource = ref<ConversationSearchResponse | null>(null);

    const { data: rawData, error: fetchError, execute } = useFetch("/v1/conversation-search/search", { immediate: false }).post(searchPayload).json<ConversationSearchResponse>();
    const isFetching = ref(false);
    const error = ref<unknown>(null);
    const searchGeneration = ref(0);

    const results = computed(() => responseSource.value?.results ?? []);
    const metadata = computed(() => responseSource.value?.metadata ?? null);

    const groupedResults = computed((): GroupedSearchResult[] => {
        const groups = new Map<string, GroupedSearchResult>();
        for (const item of results.value) {
            const key = item.session.uuid;
            const existing = groups.get(key);
            if (existing) {
                existing.results.push(item);
                existing.matchCount++;
                if (item.message.when > existing.latestDate) {
                    existing.latestDate = item.message.when;
                }
                if (item.relevance > existing.topRelevance) {
                    existing.topRelevance = item.relevance;
                    existing.topResult = item;
                }
            } else {
                groups.set(key, {
                    session: item.session,
                    results: [item],
                    topRelevance: item.relevance,
                    matchCount: 1,
                    latestDate: item.message.when,
                    topResult: item,
                });
            }
        }
        return [...groups.values()].sort((a, b) => (a.latestDate > b.latestDate ? -1 : 1));
    });

    function buildSearchPayload(searchId: string, q: string, options?: SearchOptions): { search_id: string; query: string } {
        const payload: Record<string, unknown> = { search_id: searchId, query: q };
        if (options?.scope) {
            payload.scope = options.scope;
        }
        if (options?.roles) {
            payload.roles = options.roles;
        }
        if (options?.purposes) {
            payload.purposes = options.purposes;
        }
        if (options?.content_filter) {
            payload.content_filter = options.content_filter;
        }
        if (options?.sources) {
            payload.sources = options.sources;
        }
        return payload as { search_id: string; query: string };
    }

    async function search(q: string, options?: SearchOptions) {
        const trimmed = q.trim();
        if (!trimmed) {
            return;
        }
        const searchId = globalThis.crypto.randomUUID();
        lastSearchId.value = searchId;
        query.value = trimmed;
        searchPayload.value = buildSearchPayload(searchId, trimmed, options);
        hasSearched.value = true;
        searchCount.value++;
        error.value = null;
        isFetching.value = true;
        searchGeneration.value++;
        const generation = searchGeneration.value;
        try {
            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                await execute();
                if (searchGeneration.value !== generation) {
                    break;
                }
                if (!fetchError.value && rawData.value) {
                    responseSource.value = rawData.value;
                    persist();
                    break;
                }
                if (attempt < maxAttempts) {
                    await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
                } else {
                    error.value = fetchError.value;
                }
            }
        } finally {
            if (searchGeneration.value === generation) {
                isFetching.value = false;
            }
        }

        if (searchGeneration.value === generation) {
            void logUserInteraction(AmplitudeEventNames.CONVERSATION_SEARCH_SUBMITTED, {
                search_id: searchId,
                result_count: results.value.length,
                is_refinement: searchCount.value > 1,
                scope: options?.scope ?? "chat",
            });
        }
    }

    async function refresh() {
        if (!query.value) {
            return;
        }
        await search(query.value);
    }

    function logResultClicked(item: SearchResultItem, position: number) {
        void logUserInteraction(AmplitudeEventNames.CONVERSATION_SEARCH_RESULT_CLICKED, {
            search_id: lastSearchId.value,
            session_uuid: item.session.uuid,
            message_id: item.message.id,
            relevance: item.relevance,
            result_position: position,
        });
    }

    function reset() {
        query.value = "";
        searchPayload.value = null;
        hasSearched.value = false;
        searchCount.value = 0;
        lastSearchId.value = "";
        responseSource.value = null;
        error.value = null;
        isFetching.value = false;
        searchGeneration.value++;
        try {
            sessionStorage.removeItem(STORAGE_KEY);
        } catch {
            // Storage unavailable (e.g. private mode quota); ignore.
        }
    }

    function persist() {
        try {
            const state: PersistedState = {
                query: query.value,
                response: responseSource.value,
                hasSearched: hasSearched.value,
                searchCount: searchCount.value,
                lastSearchId: lastSearchId.value,
            };
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch {
            // sessionStorage is unavailable (private-browsing quota or disabled);
            // back-nav restore won't work but the live in-memory state still does.
        }
    }

    function hydrate(): boolean {
        try {
            const raw = sessionStorage.getItem(STORAGE_KEY);
            if (!raw) {
                return false;
            }
            const state = JSON.parse(raw) as PersistedState;
            if (!state.hasSearched || !state.response) {
                return false;
            }
            query.value = state.query;
            responseSource.value = state.response;
            hasSearched.value = state.hasSearched;
            searchCount.value = state.searchCount;
            lastSearchId.value = state.lastSearchId;
            return true;
        } catch {
            // Stored payload is malformed (likely an older shape after a
            // composable refactor) — clear it so the next persist starts clean.
            try {
                sessionStorage.removeItem(STORAGE_KEY);
            } catch {
                // Storage itself is unavailable; nothing to clear.
            }
            return false;
        }
    }

    return {
        query,
        results,
        metadata,
        groupedResults,
        isFetching,
        error,
        hasSearched,
        searchCount,
        lastSearchId,
        search,
        refresh,
        logResultClicked,
        reset,
        hydrate,
    };
}
